import { type ReactNode, useEffect, useMemo, useState } from 'react'
import useSWR from 'swr'
import { useProjectStore } from '@/hooks/use-project-store'
import { isFileSystemTreeListContainsSupabase } from '@/lib/supabase/utils'
import type {
  ProjectTable,
  TableColumnDefinition,
  TableFieldDefinition,
} from '@/lib/types'
import { fetcher, mapKeyToObject } from '@/lib/utils'
import { getTablesUrl, getUsersUrl } from './constant'
import { DatabaseContext } from './context'
import { useCRUD } from './hooks/useCrud'
import { isNil } from 'lodash'
import { PaginationClass } from '@/shared'
import { getFileSystemTreeSchemas } from '@/lib/file-system-tree'

export function DatabaseProvider(props: {
  children: ReactNode
  tableName?: string
}) {
  const { children } = props
  const { project, updateProjectTableColumnsToDBAndSyncToStore } =
    useProjectStore()
  const [usersPagination, setUsersPagination] = useState<PaginationClass>(
    new PaginationClass(),
  )
  const [tableName, setTableName] = useState(props.tableName || '')

  const [tableNameToPagination, setTableNameToPagination] = useState<
    Record<string, PaginationClass>
  >({})

  const projectId = useMemo(() => project?._id?.toString(), [project?._id])

  const tablesUrl = project ? getTablesUrl(project._id.toString()) : null
  const usersUrl = project ? getUsersUrl(project._id.toString()) : null
  const isSupabaseEnabled = useMemo(
    () =>
      project &&
      (isFileSystemTreeListContainsSupabase(project.file_system_tree_list) ||
        getFileSystemTreeSchemas(project.file_system_tree!).length > 0),
    [project],
  )

  // 每次启动弹窗更新 tableName
  useEffect(() => {
    tableName && setTableName(tableName)
  }, [tableName])

  // 初始化请求 users
  const {
    data: users,
    isLoading: isLoadingUsers,
    mutate: refetchUsers,
  } = useSWR(usersUrl, fetcher)

  useEffect(() => {
    if (users) {
      setUsersPagination(
        new PaginationClass(users.page, users.perPage, users.total),
      )
    }
  }, [users])

  // // 初始化请求 tables
  const {
    data: tables,
    isLoading: isLoadingTables,
    mutate: refetchTables,
  } = useSWR<ProjectTable[]>(isSupabaseEnabled ? tablesUrl : null, fetcher)

  // set the first table as the default table
  useEffect(() => {
    if (users && users.data.length > 0) {
      if (!tableName) {
        setTableName('users')
        return
      }
    }

    if (tables && tables.length > 0) {
      if (!tableName) {
        setTableName(tables[0].name)
      }
    }
  }, [users, tables, tableName])

  useEffect(() => {
    if (tables && tables.length > 0) {
      for (const table of tables) {
        if (!tableNameToPagination[table.name]) {
          setTableNameToPagination((prev) => ({
            ...prev,
            [table.name]: new PaginationClass(),
          }))
        }
      }
    }
  }, [tables, tableNameToPagination])

  const tableNameToTable = useMemo(
    () => mapKeyToObject(tables || [], 'name'),
    [tables],
  )

  const selectedTable = useMemo(() => {
    return tableName ? tableNameToTable[tableName] : null
  }, [tableNameToTable, tableName])

  const selectedTableColumn = useMemo(() => {
    const projectTableColumns = (project?.table_columns || []).find(
      (tableCol) => tableCol.db_table_name === tableName,
    )

    const supabaseTable = (project?.supabase_tables || []).find(
      (table) => table.name === tableName,
    )

    if (!supabaseTable) return projectTableColumns
    const { columns } = supabaseTable

    return {
      ...projectTableColumns,
      field_list: (projectTableColumns?.field_list || [])
        .map((d) => {
          const column = columns.find((col) => col.name === d.db_name)
          if (!column) return false
          return {
            ...d,
            // 优先以数据库为准，ai 生成结果不可靠
            required: !isNil(column.nullable) ? !column.nullable : d.required,
            db_type: column?.type,
          }
        })
        .filter((d) => !!d),
    } as TableColumnDefinition
  }, [project, tableName])

  const crud = useCRUD({
    projectId,
    tableDef: selectedTable,
    pagination: tableName ? tableNameToPagination[tableName] : undefined,
    setTableNameToPagination,
  })

  // ✅ 封装统一 loading 状态对象
  const loading = {
    users: isLoadingUsers,
    tables: isLoadingTables,
    crud: crud.loading,
  }

  return (
    <DatabaseContext.Provider
      value={{
        tableName,
        setTableName,
        project,
        isSupabaseEnabled,
        tables,
        selectedTable,
        selectedTableColumn,
        usersPagination,
        setUsersPagination,
        usersData: users?.data || [],
        refetchUsers,
        refetchTables,
        normalTableColumns: crud.columns,
        normalTableData: crud.list,
        normalTablePagination: crud.pagination,
        setNormalTablePagination: setTableNameToPagination,
        crud,
        loading,
        updateProjectTableColumnsToDBAndSyncToStore,
      }}
    >
      {children}
    </DatabaseContext.Provider>
  )
}
